import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Box, Typography } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import React, { useEffect, useState } from "react";

import Highlight from "../../../../Highlight";
import { Worker } from "../../../store/full-time-shifts-type";
import { NON_MOVABLE_STATUS_CODES } from "../constants";
import { StatusChip } from "./status-chip";

interface StatusOption {
  id: number;
  label: string;
  action: (worker: Worker) => void;
}

export const WorkerItem = ({
  worker,
  search,
  pinnedWorker,
  hasPermissionToReassignWorkers,
  fadeOut = false,
  index,
  totalWorkersCount,
  getAvailableStatusOptions,
  statusChipStyles,
  onMouseDown,
  onMouseUp,
  lastMovedWorker = false,
}: {
  worker: Worker;
  search: string;
  pinnedWorker?: Worker | null;
  hasPermissionToReassignWorkers: boolean;
  fadeOut?: boolean;
  index: number;
  totalWorkersCount: number;
  getAvailableStatusOptions: (currentCode: string, isMoved: boolean, originalStatus: string) => StatusOption[];
  statusChipStyles: Record<string, any>;
  onMouseDown: (e: React.MouseEvent, worker: Worker) => void;
  onMouseUp: (e: React.MouseEvent, worker: Worker) => void;
  lastMovedWorker?: boolean;
}) => {

  const [isFadingOut, setIsFadingOut] = useState(false);

  useEffect(() => {
    if (fadeOut) {
      // Use a timeout to stagger the fade-out effect.
      // The delay is calculated to make higher indices fade out first.
      let delay = (totalWorkersCount - index) * 50; // 50ms per worker
      if (delay > 1000) {
        delay = 1000;
      }
      setTimeout(() => {
        setIsFadingOut(true);
      }, delay);
    } else {
      setIsFadingOut(false);
    }
  }, [fadeOut, index, totalWorkersCount]);

  const isNonMovable =
    NON_MOVABLE_STATUS_CODES.includes(worker.status?.code) ||
    !hasPermissionToReassignWorkers;

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "row",
      width: "100%",
      transition: "opacity 0.3s ease-in-out, height 0.3s ease-in-out",
      opacity: isFadingOut ? 0 : 1,
      height: isFadingOut ? "0px" : "58px",
      marginTop: isFadingOut ? "0px" : index !== 0 ? "4px" : "0px",
      marginBottom: isFadingOut ? "0px" : "4px",
    }}>
      <Box sx={{
        width: `${(35 / 55) * 100}%`,
      }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            overflow: "hidden",
            maxWidth: "320px",
          }}
        >
          <Box
            onMouseDown={(e) => {
              setTimeout(() => {
                if (!isNonMovable && !pinnedWorker) {
                  onMouseDown(e, worker);
                }
              }, 200)
            }}
            onMouseUp={(e) => {
              if (!isNonMovable) {
                onMouseUp(e, worker);
              }
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: lastMovedWorker ? "#e0e0e0" : "#eee",
              opacity: pinnedWorker?.id === worker.id ? 0.6 : 1,
              padding: "8px 12px 8px 4px",
              borderRadius: "4px",
              cursor: pinnedWorker
                ? "default"
                : (NON_MOVABLE_STATUS_CODES.includes(worker.status?.code)
                  || !hasPermissionToReassignWorkers)
                  ? "not-allowed"
                  : "grab",
              width: "100%",
              maxWidth: "320px",
              height: "58px",
              border: worker.moved ? "1px dashed rgba(0,0,0,0.6)" : "1px solid transparent",
              gap: "4px",
            }}
          >
            <DragIndicatorIcon
              sx={{
                fontSize: 24,
                color: (NON_MOVABLE_STATUS_CODES.includes(worker.status?.code)
                  || !hasPermissionToReassignWorkers)
                  ? "rgba(0,0,0,0.2)"
                  : "rgba(0,0,0,0.54)",
              }}
            />
            <Avatar
              sx={{
                backgroundColor: "#e0e0e0",
                border: "1px solid rgba(0,0,0,0.14)",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "16px",
                  color: "rgba(0,0,0,0.87)",
                  width: "fit-content",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                  userSelect: "none",
                }}
              >
                <Highlight value={worker.initials} searchText={search} />
              </Typography>
            </Avatar>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "center",
                maxWidth: "65%",
                paddingLeft: "4px",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "14px",
                  color: "rgba(0,0,0,0.87)",
                  width: "fit-content",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                  userSelect: "none",
                }}
              >
                <Highlight searchText={search} value={worker.name} />
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "12px",
                  color: "rgba(0,0,0,0.6)",
                  width: "fit-content",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                  userSelect: "none",
                }}
              >
                <Highlight searchText={search} value={worker.info} />
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{
        width: `${(20 / 55) * 100}%`,
      }}>
        <StatusChip
          key={`worker-${worker.id}-${index}`}
          worker={worker}
          search={search}
          hasPermission={hasPermissionToReassignWorkers}
          getAvailableStatusOptions={getAvailableStatusOptions}
          statusChipStyles={statusChipStyles}
        />
      </Box>
    </Box>
  );
};
